/* =========================================================================
   Rank ladder — bundled mirror of the 25-rank seed (db/001:661-676) so the
   client can compute rank + brightness tier for Brother Theo offline / for
   guests (who never hit /profile/me). XP curve is fast early, long tail.
   Keep in sync with app.RankLevels if the seed is ever re-tuned in /sadmin.
   ========================================================================= */
export interface Rank {
  level: number;
  name: string;
  xpRequired: number;
  icon: string;
}

export const RANKS: readonly Rank[] = [
  { level: 1, name: 'Seeker', xpRequired: 0, icon: '🕯️' },
  { level: 2, name: 'Inquirer', xpRequired: 500, icon: '📖' },
  { level: 3, name: 'Catechumen', xpRequired: 1500, icon: '💧' },
  { level: 4, name: 'Neophyte', xpRequired: 3000, icon: '🌱' },
  { level: 5, name: 'Disciple', xpRequired: 5000, icon: '🐟' },
  { level: 6, name: 'Pilgrim', xpRequired: 8000, icon: '🥾' },
  { level: 7, name: 'Steward', xpRequired: 12000, icon: '🗝️' },
  { level: 8, name: 'Lamplighter', xpRequired: 17000, icon: '🏮' },
  { level: 9, name: 'Storyteller', xpRequired: 23000, icon: '📜' },
  { level: 10, name: 'Scholar', xpRequired: 30000, icon: '🎓' },
  { level: 11, name: 'Evangelist', xpRequired: 40000, icon: '🕊️' },
  { level: 12, name: 'Missionary', xpRequired: 52000, icon: '🌍' },
  { level: 13, name: 'Catechist', xpRequired: 66000, icon: '✏️' },
  { level: 14, name: 'Apologist', xpRequired: 82000, icon: '🛡️' },
  { level: 15, name: 'Contemplative', xpRequired: 100000, icon: '🙏' },
  { level: 16, name: 'Peacemaker', xpRequired: 125000, icon: '🕊️' },
  { level: 17, name: 'Faithful Witness', xpRequired: 155000, icon: '⭐' },
  { level: 18, name: "Shepherd's Helper", xpRequired: 190000, icon: '🐑' },
  { level: 19, name: 'Keeper of Tradition', xpRequired: 230000, icon: '🏛️' },
  { level: 20, name: 'Church Historian', xpRequired: 280000, icon: '🗿' },
  { level: 21, name: 'Theologian', xpRequired: 340000, icon: '📚' },
  { level: 22, name: 'Master Catechist', xpRequired: 410000, icon: '👑' },
  { level: 23, name: 'Pillar of Faith', xpRequired: 500000, icon: '⛪' },
  { level: 24, name: 'Saintly Scholar', xpRequired: 620000, icon: '😇' },
  { level: 25, name: 'Doctor of the Church', xpRequired: 800000, icon: '🏆' },
];

export type Tier = 1 | 2 | 3 | 4 | 5;

/** Highest rank the given XP total has reached (clamped to Seeker at/below 0 XP). */
export function rankForXp(xp: number): Rank {
  let result = RANKS[0];
  for (const r of RANKS) {
    if (xp >= r.xpRequired) result = r;
    else break;
  }
  return result;
}

/** Brightness tier (1-5) for Theo's art — 5 tiers evenly over the 25 ranks. */
export function tierForRank(level: number): Tier {
  if (level <= 0) return 1;
  const t = Math.ceil(level / 5);
  return Math.min(5, Math.max(1, t)) as Tier;
}
